import React, { useEffect, useMemo, useState } from "react";
import { Form, ImageField, SelectField, StringField, SubmitButton } from "@culturehq/components";
import { Trans } from "app/utils";
import Tip from "components/Tip";
import ExclamationIcon from "components/common/ExclamationIcon";
import AcceptPolicies from "components/login/AcceptPolicies";
import { useUserSelectOptions } from "app/context";
import { makeGet } from "@culturehq/client";
import { AuthorizedFields, UserInformationState, UserInformationValues } from "../../types";

interface FormFieldProps {
  key: string;
  mandatory: boolean;
}
class FormField {
  key: keyof typeof AuthorizedFields;;

  mandatory: boolean;

  constructor({ key, mandatory }: FormFieldProps) {
    this.key = this.transformKey(key);
    this.mandatory = mandatory;
  }

  // eslint-disable-next-line class-methods-use-this
  transformKey(key: string) {
    const replacementKeys: any = {
      user_name: "name",
      avatar_url: "avatarUrl",
      location_name: "locationName",
      department_names: "departmentNames"
    };
    return replacementKeys[key as keyof typeof replacementKeys] || key;
  }
}

interface MoreUserInfoFormProps {
  values: UserInformationValues;
  agreed: UserInformationState["agreed"];
  isAllPoliciesChecked: boolean;
  policiesAgreed: UserInformationState["policiesAgreed"];
  submitButtonLabel: string;
  organization: UserInformationState["organization"];
  onChange: (newState: UserInformationValues) => void;
  onStorySubmit: () => void;
  onAgreeChange: (agreed: UserInformationState["agreed"]) => void;
  onPrivacyPolicyChange: (value: boolean, index: number) => void;
}

const defaultFormFields: Record<AuthorizedFields, boolean> = {
  [AuthorizedFields.name]: true,
  [AuthorizedFields.avatarUrl]: true,
  [AuthorizedFields.title]: true,
  [AuthorizedFields.locationName]: true,
  [AuthorizedFields.departmentNames]: true
};

const MoreUserInfoForm: React.FC<MoreUserInfoFormProps> = ({
  agreed,
  isAllPoliciesChecked,
  policiesAgreed,
  submitButtonLabel,
  organization,
  onChange,
  onStorySubmit,
  onAgreeChange,
  onPrivacyPolicyChange,
  values
}) => {
  const [fields, setFields] = useState<Record<AuthorizedFields, boolean>>();
  const { departmentOptions, locationOptions } = useUserSelectOptions();

  useEffect(() => {
    let isMounted = true;

    const parseFormFields = (fields: any[]) => fields.reduce((acc, f) => {
      const field = new FormField(f);

      if (acc[field.key]) return acc;

      acc[field.key] = field.mandatory;
      return acc;
    }, { name: true });

    makeGet("/stories/public/user_form_fields", { organizationId: organization.id as number })
      .then((form: any) => isMounted && setFields(
        form.fields.length ? parseFormFields(form.fields) : defaultFormFields
      )).catch(() => setFields(defaultFormFields));

    return () => {
      isMounted = false;
    };
  }, [organization.id]);

  const areMandatoryFieldsFilled = useMemo(
    () => fields && Object.keys(fields).filter(key => fields[key as AuthorizedFields]).every(
      key => {
        const value = values[key as keyof UserInformationValues];
        if (key === "name" && !value) {
          const firstNameValue = values["firstName" as keyof UserInformationValues];
          const lastNameValue = values["lastName" as keyof UserInformationValues];
          const checkedFirstNameValue = firstNameValue !== undefined && firstNameValue !== "" && firstNameValue !== null;
          const checkedLastNameValue = lastNameValue !== undefined && lastNameValue !== "" && lastNameValue !== null;
          return checkedFirstNameValue && checkedLastNameValue;
        }
        const hasDataValue = Object.prototype.hasOwnProperty.call(values, key);
        const checkedValue = value !== undefined && value !== "" && value !== null && (!Array.isArray(value) || !!value.length);
        return hasDataValue && checkedValue;
      }
    ), [fields, values]);

  const isDisabledSubmit = !areMandatoryFieldsFilled || !agreed || !isAllPoliciesChecked;

  return (
    <Form
      aria-label="form"
      className="Form"
      onSubmit={onStorySubmit}
      initialValues={values}
    >
      <div className="MoreUserInfo">
        <Tip
          message={(
            <strong>
              Looks like you haven&apos;t confirmed your profile details yet.
              Please fill out the info below and then submit your story!
            </strong>
          )}
          SvgIcon={<ExclamationIcon />}
        />

        <ImageField
          name="avatarUrl"
          aspectRatio={1}
          backgroundIcon="user-filled"
          onChange={avatarUrl => onChange({ avatarUrl })}
          progress={0}
          required={fields?.avatarUrl}
        >
          <Trans>Profile Photo</Trans>
          {fields?.avatarUrl && "*"}
        </ImageField>

        {organization.firstNameLastNameEnabled ? (
          <>
            <StringField className="NameField" name="firstName" onChange={firstName => onChange({ firstName })} required>
              <Trans>First Name{" "}{fields?.name && "*"}</Trans>
            </StringField>
            <StringField className="NameField" name="lastName" onChange={lastName => onChange({ lastName })} required>
              <Trans>Last Name{" "}{fields?.name && "*"}</Trans>
            </StringField>
          </>
        ) : (
          <StringField
            className="NameField"
            name="name"
            onChange={name => onChange({ name })}
            required={fields?.name}
          >
            <Trans>Name{" "}{fields?.name && "*"}</Trans>
          </StringField>
        )}

        <StringField name="title" onChange={title => onChange({ title })} required={fields?.title}>
          <Trans>Title</Trans>
          {fields?.title && "*"}
        </StringField>

        <SelectField
          name="locationName"
          creatable
          options={locationOptions}
          onChange={(locationName: any) => onChange({ locationName })}
          required={fields?.locationName}
        >
          <Trans>Location</Trans>
          {fields?.locationName && "*"}
        </SelectField>

        <SelectField
          name="departmentNames"
          creatable
          multiple
          options={departmentOptions}
          onChange={(departmentNames: any) => onChange({ departmentNames })}
          required={fields?.departmentNames}
        >
          <Trans>Departments</Trans>
          {fields?.departmentNames && "*"}
        </SelectField>

        <div className="Policies">
          <AcceptPolicies
            agreed={agreed}
            policiesAgreed={policiesAgreed}
            organization={organization}
            onAgreeChange={onAgreeChange}
            onPrivacyPolicyChange={onPrivacyPolicyChange}
          />
        </div>
      </div>

      <SubmitButton className="Submit" primary disabled={isDisabledSubmit}>
        {submitting => (submitting ? (
          <Trans>Posting...</Trans>
        ) : (
          <Trans>{submitButtonLabel}</Trans>
        ))}
      </SubmitButton>
    </Form>
  );
};

export default MoreUserInfoForm;
